import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  StudioProvider,
  useStudio,
  StudioHeader,
  PipelineBar,
  BriefPanel,
  AnglesPanel,
  ScriptPanel,
  ReviewPanel,
  AssetsPanel,
  type StudioConfig,
  type StudioPanelId,
} from '@neurograph/skill-apps';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const PANELS: Record<StudioPanelId, React.ComponentType> = {
  brief: BriefPanel,
  angles: AnglesPanel,
  script: ScriptPanel,
  review: ReviewPanel,
  assets: AssetsPanel,
};

export function StudioPage() {
  const { briefId, clientUid } = useParams<{ briefId?: string; clientUid?: string }>();

  const resolvedClientUid = clientUid || 'default';

  const config: StudioConfig = {
    session: {
      api_base: API_BASE,
      client_uid: resolvedClientUid,
      mode: 'workbench',
    },
    client_info: {
      uid: resolvedClientUid,
      name: resolvedClientUid,
      category: '',
    },
    brand_tokens: {},
    personas: [],
    initial_data: {
      brief: null,
      persona_id: null,
      mode: 'workbench',
    },
    api_endpoints: {
      briefs: `${API_BASE}/api/v1/generation/briefs`,
      brief: briefId ? `${API_BASE}/api/v1/generation/briefs/${briefId}` : null,
      personas: `${API_BASE}/api/v1/skills/personas/${resolvedClientUid}`,
      brand_tokens: `${API_BASE}/api/v1/brand-design/status/${resolvedClientUid}/tokens`,
      skills_invoke: `${API_BASE}/api/v1/skills/invoke`,
    },
    delivery_mode: 'admin',
  };

  // Render studio directly without MCP hook dependency
  return (
    <StudioProvider config={config}>
      <StudioLayout />
    </StudioProvider>
  );
}

function StudioLayout() {
  const {
    config,
    activePanel,
    setActivePanel,
    pipelineStatus,
    error,
    brandTokens,
  } = useStudio();

  const ActivePanel = PANELS[activePanel] || BriefPanel;
  const clientName = config.client_info?.name || config.session?.client_uid || 'Creative Studio';
  const mode = config.initial_data?.mode || config.session?.mode || 'workbench';
  const agencyName = brandTokens?.agency_theme?.name;

  // Apply brand theming via CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const client = brandTokens?.client_theme || {};
    Object.entries(client).forEach(([k, v]) => {
      if (v && k.startsWith('--')) root.style.setProperty(k, String(v));
    });
    const agency = brandTokens?.agency_theme || {};
    if (agency.chrome_bg) root.style.setProperty('--ng-chrome-bg', agency.chrome_bg);
    if (agency.chrome_text) root.style.setProperty('--ng-chrome-text', agency.chrome_text);
  }, [brandTokens]);

  return (
    <div className="bg-neutral-50 text-neutral-900">
      <StudioHeader clientName={clientName} mode={mode} agencyName={agencyName} />
      <PipelineBar
        steps={pipelineStatus}
        activePanel={activePanel}
        onStepClick={(id) => setActivePanel(id)}
      />
      {error && (
        <div className="bg-red-50 text-red-800 px-5 py-2 text-sm border-b border-red-200">
          {error}
        </div>
      )}
      <main className="max-w-4xl mx-auto px-5 py-6">
        <ActivePanel />
      </main>
    </div>
  );
}
